/*
 * Universal-mode replacement for `@shopify/app-bridge-react`.
 *
 * Vite aliases `@shopify/app-bridge-react` to this module (see vite.config.js),
 * so the existing pages that do `import { SaveBar, useAppBridge } from
 * '@shopify/app-bridge-react'` keep working in standalone mode without edits.
 *
 * - useAppBridge(): returns an object exposing the bits the app uses
 *   (`saveBar.hide/show/leaveConfirmation`, `toast`). The save bar methods are
 *   no-ops because <SaveBar> below renders itself inline.
 * - SaveBar: renders a sticky top contextual bar (Shopify-style) containing the
 *   page's own <button> children.
 */

import React from 'react';
import { createPortal } from 'react-dom';
import './shopifyGlobal';

export function useAppBridge() {
  const toast = (typeof window !== 'undefined' && window.shopify && window.shopify.toast) || {
    show: () => {},
    hide: () => {},
  };

  return {
    toast,
    loading: () => {},
    saveBar: {
      show: () => {},
      hide: () => {},
      // App Bridge shows a "discard unsaved changes?" dialog. Mirror that with a
      // native confirm: resolve => proceed (leave), reject => stay.
      leaveConfirmation: () =>
        (typeof window !== 'undefined' &&
        window.confirm('You have unsaved changes. Leave without saving?'))
          ? Promise.resolve()
          : Promise.reject(new Error('leave-cancelled')),
    },
  };
}

const discardButtonStyle = {
  padding: '8px 16px',
  borderRadius: '8px',
  fontWeight: 500,
  fontSize: '14px',
  cursor: 'pointer',
  border: '1px solid rgba(255,255,255,0.35)',
  background: 'transparent',
  color: '#ffffff',
};

const primaryButtonStyle = {
  padding: '8px 16px',
  borderRadius: '8px',
  fontWeight: 600,
  fontSize: '14px',
  cursor: 'pointer',
  border: '1px solid #008060',
  background: '#008060',
  color: '#ffffff',
};

const SAVE_BAR_SLOT_ID = 'app-save-bar-root';

export function SaveBar({ open, children, message = 'Unsaved changes' }) {
  // The Frame renders a slot right under the sticky app header; the bar
  // portals into it so it stays docked to the header while scrolling.
  const [slot, setSlot] = React.useState(() =>
    typeof document !== 'undefined' ? document.getElementById(SAVE_BAR_SLOT_ID) : null
  );

  React.useEffect(() => {
    if (!slot) {
      setSlot(document.getElementById(SAVE_BAR_SLOT_ID));
    }
  }, [slot]);

  // Visible when mounted unless explicitly closed (open === false).
  if (open === false) return null;

  const styledChildren = React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;
    const isPrimary = child.props.variant === 'primary';
    return React.cloneElement(child, {
      style: {
        ...(isPrimary ? primaryButtonStyle : discardButtonStyle),
        ...(child.props.style || {}),
      },
    });
  });

  const bar = (
    <div className="app-save-bar">
      <span className="app-save-bar__message">{message}</span>
      <div className="app-save-bar__actions">{styledChildren}</div>
    </div>
  );

  if (slot) {
    return createPortal(bar, slot);
  }

  // No Frame slot (e.g. auth screens): fall back to a sticky inline bar.
  return (
    <div style={{ position: 'sticky', top: 0, zIndex: 400, marginBottom: '16px' }}>{bar}</div>
  );
}

// Defensive no-op exports in case other App Bridge APIs get imported.
export const Provider = ({ children }) => children;
export const TitleBar = () => null;
export const NavigationMenu = () => null;

export default { useAppBridge, SaveBar, Provider, TitleBar, NavigationMenu };
